// ────────────────────────────────────────────────────────────────────
// TodayScreen — ถาดงานวันนี้ (workbench roadmap A2). Replaces the old
// "/" -> "/atlas" redirect: home now answers "what needs a decision today"
// instead of "where are my sites" (nobody asks that at 9am). Atlas demotes
// to an ordinary tool, reachable from the menu bar.
//
// 2026-09-01 — TODAY is THE work queue (spec: 2026-09-01-today-is-the-queue-
// design.md). It absorbed SubmissionsScreen's left half: the status filter is
// here, its rows open JOB at /jobs/:id, and รอตรวจ — which spent a whole
// route (PendingReviewsScreen, now deleted) on 56 lines and one query — is a
// filter. TODAY keeps its "assets ใกล้ครบกำหนด" section (plan ruling 1): an
// asset approaching due with no งาน against it is work that does not exist
// yet, and a queue made only of งาน hides exactly that.
//
// 2026-09-01, Task 3 — "/" and "/submissions" retired into this screen, so it
// is the landing as well as the queue. Two things came with them: "งานใหม่"
// (the only free-form way to OPEN a งาน — the file room opens one from files,
// the chat dock from a conversation, neither takes a title or an intent), and
// the รอตรวจ count that used to be a badge in the header.
//
// Every section is composed from endpoints that already exist elsewhere in
// the app — no new backend work for this screen:
//   • งานที่ตาเรา / งานที่รอคุณ / งานที่รอลูกค้า — apiClient.submissions() +
//     the SAME ballInCourt derivation SubmissionsScreen's Work queue and the
//     inbox context pane already share (TeamChatDock.tsx).
//   • บทสนทนาที่ยังไม่ตอบ — staff: apiClient.inbox()'s awaiting_reply_since
//     (already tenant-agnostic + pre-sorted oldest-first server-side).
//     Customer: the same computeUnread/lastTeamMessageAt/getSeenWatermark
//     rule that drives the chat dock's own unread pill.
//   • Assets ใกล้ครบกำหนด/เกินกำหนด — apiClient.plants() + the per-plant
//     apiClient.plantCompliance() the CompliancePanel already renders,
//     merged across every plant in scope (active tenant for staff, the
//     customer's own tenant for a customer — same RLS scope as the rest of
//     the app either way).
//
// OMITTED deliberately: a "readings flagged as suspicious" section — that
// data only exists as a per-inspection concern-ranking used by the corrosion
// engine's chat metrics, not as a queryable list across all assets. Surfacing
// it honestly needs a new aggregate endpoint (Stage B/C work), so it's left
// out rather than faked from a shape that doesn't fit.
// ────────────────────────────────────────────────────────────────────
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import { ArrowRight, ClipboardList, Clock, FileWarning, MessageSquare, Plus } from "lucide-react";
import { apiClient, getCachedUser, STAFF_ROLES } from "@/lib/api";
import type { Submission, SubmissionStatus, ComplianceRow, InboxRow, PendingReview } from "@/lib/types";
import { ballInCourt, ballTriageCompare, computeUnread, lastTeamMessageAt, getSeenWatermark, openChatForTenant } from "@/components/TeamChatDock";
import { isOverdue, CreateModal } from "./JobScreen";
import { AwaitingBadge } from "./InboxScreen";

// Oldest-first by whichever timestamp reflects when it started waiting.
function byOldest(a: string, b: string): number {
  return Date.parse(a) - Date.parse(b);
}

function daysWaiting(iso: string): string {
  const d = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  return d <= 0 ? "วันนี้" : d === 1 ? "1 วัน" : `${d} วัน`;
}

interface DueRow extends ComplianceRow {
  plant_name: string;
}

// ── The work queue's filters (moved here 2026-09-01, spec: TODAY is the
// queue). Six of them ARE SubmissionsScreen's left-half filter, unchanged in
// meaning. Two are new:
//
//   • "mine" (ตาเรา) is the default and is what TODAY always showed — the
//     ball-in-court list, oldest-waiting first. It is a filter now rather
//     than the only thing the section could be.
//   • "review" (รอตรวจ) is NOT a status of a งาน. It was a whole route
//     (PendingReviewsScreen, deleted with this change) over a DIFFERENT
//     domain: assets whose sign-off is open, from /api/reviews/pending. No
//     client-side filter over submissions could ever return it — submissions
//     have no review state — so this lens reads that exact endpoint. That is
//     why "รอตรวจ returns exactly what the route returned" is provable
//     rather than approximated.
type QueueFilter = "mine" | "open" | "overdue" | "needs_info" | "in_progress" | "completed" | "all" | "review";

const OPEN_STATUSES: SubmissionStatus[] = ["submitted", "acknowledged", "in_progress", "needs_info"];

const QUEUE_FILTERS: { key: QueueFilter; label: string; title: string; empty: string }[] = [
  { key: "mine",        label: "ตาเรา",     title: "งานที่ตาเรา",              empty: "ไม่มีงานค้างที่ตาเรา" },
  { key: "open",        label: "ค้างอยู่",   title: "งานที่ยังไม่ปิด",           empty: "ไม่มีงานค้าง" },
  { key: "overdue",     label: "เกินกำหนด",  title: "งานเกินกำหนด",             empty: "ไม่มีงานเกินกำหนด" },
  { key: "needs_info",  label: "รอข้อมูล",   title: "งานที่รอข้อมูลจากลูกค้า",    empty: "ไม่มีงานที่รอข้อมูล" },
  { key: "in_progress", label: "กำลังทำ",    title: "งานที่กำลังดำเนินการ",       empty: "ไม่มีงานที่กำลังดำเนินการ" },
  { key: "completed",   label: "เสร็จแล้ว",  title: "งานที่เสร็จแล้ว",           empty: "ยังไม่มีงานที่เสร็จ" },
  { key: "all",         label: "ทั้งหมด",    title: "งานทั้งหมด",               empty: "ไม่มีงาน" },
  { key: "review",      label: "รอตรวจ",     title: "รอตรวจ",                  empty: "ไม่มีรายการรอตรวจ" },
];

function isQueueFilter(v: string | null): v is QueueFilter {
  return !!v && QUEUE_FILTERS.some((f) => f.key === v);
}

function useDueAssets() {
  const plantsQ = useQuery({
    queryKey: ["plants", { active_only: true }],
    queryFn: () => apiClient.plants({ active_only: true }),
    staleTime: 60_000,
  });
  const plantIds = (plantsQ.data ?? []).map((p) => p.id);
  const dueQ = useQuery({
    queryKey: ["today-due-assets", plantIds.join(",")],
    queryFn: async (): Promise<DueRow[]> => {
      const plants = plantsQ.data ?? [];
      const perPlant = await Promise.all(
        plants.map((p) => apiClient.plantCompliance(p.id)
          .then((c) => ({ plant_name: p.name, rows: c.rows }))
          .catch(() => null)),
      );
      const rows: DueRow[] = [];
      for (const p of perPlant) {
        if (!p) continue;
        for (const r of p.rows) {
          if (r.state === "overdue" || r.state === "due_soon") rows.push({ ...r, plant_name: p.plant_name });
        }
      }
      rows.sort((a, b) => {
        if (a.state !== b.state) return a.state === "overdue" ? -1 : 1;
        return (a.next_due || "").localeCompare(b.next_due || "");
      });
      return rows;
    },
    enabled: !plantsQ.isPending,
    staleTime: 60_000,
  });
  return { isPending: plantsQ.isPending || dueQ.isPending, rows: dueQ.data ?? [] };
}

export default function TodayScreen(): JSX.Element {
  const navigate = useNavigate();
  const user = getCachedUser();
  const isStaff = !!user && (STAFF_ROLES as readonly string[]).includes(user.role);

  const subsQ = useQuery({ queryKey: ["submissions"], queryFn: () => apiClient.submissions(), staleTime: 15_000 });
  const subs: Submission[] = subsQ.data ?? [];
  const teamBall = useMemo(
    () => subs.filter((s) => ballInCourt(s.status) === "team")
               .slice().sort((a, b) => byOldest(a.updated_at ?? a.created_at, b.updated_at ?? b.created_at)),
    [subs],
  );
  // The customer's own งาน that are sitting on THEIR court (needs_info) —
  // the mirror image of teamBall. Used as the actionable list on the
  // customer view ("งานที่รอคุณ") and as the quiet count on the staff view
  // ("งานที่รอลูกค้า") — never the other way around (a bug this screen's own
  // test suite caught: reusing teamBall for both roles silently showed staff
  // rows to customers).
  const customerBall = useMemo(
    () => subs.filter((s) => ballInCourt(s.status) === "customer")
               .slice().sort((a, b) => byOldest(a.updated_at ?? a.created_at, b.updated_at ?? b.created_at)),
    [subs],
  );

  // The filter is read from the URL once, so /reviews (and any bookmark)
  // still lands on the list it named. It is state after that — clicking a
  // chip is not navigation.
  const [params] = useSearchParams();
  const [filter, setFilter] = useState<QueueFilter>(
    isQueueFilter(params.get("queue")) ? (params.get("queue") as QueueFilter) : "mine",
  );

  // The header's รอตรวจ badge was keyed to the retired /reviews and went with
  // it (2026-09-01); re-keying a count of assets-awaiting-signature to
  // ถาดงานวันนี้ would have read as "N things today" and been a lie. The count
  // lives HERE now, on the chip, and "/" lands here — so it is on the first
  // screen after sign-in instead of behind a click-to-open menu panel.
  const reviewsQ = useQuery({
    queryKey: ["pending-reviews"], queryFn: () => apiClient.pendingReviews(),
    enabled: isStaff, staleTime: 15_000,
  });
  const reviews: PendingReview[] = reviewsQ.data ?? [];

  // The queue under a งาน filter. "mine" keeps TODAY's original list and
  // ordering untouched; everything else is SubmissionsScreen's left half,
  // ordered by the shared ballTriageCompare.
  const filtered = useMemo(() => {
    if (filter === "mine") return teamBall;
    const rows = filter === "all" ? subs
      : filter === "open" ? subs.filter((s) => OPEN_STATUSES.includes(s.status))
      : filter === "overdue" ? subs.filter(isOverdue)
      : subs.filter((s) => s.status === filter);
    return rows.slice().sort(ballTriageCompare);
  }, [subs, teamBall, filter]);

  const counts = useMemo((): Record<QueueFilter, number> => ({
    mine: teamBall.length,
    open: subs.filter((s) => OPEN_STATUSES.includes(s.status)).length,
    overdue: subs.filter(isOverdue).length,
    needs_info: subs.filter((s) => s.status === "needs_info").length,
    in_progress: subs.filter((s) => s.status === "in_progress").length,
    completed: subs.filter((s) => s.status === "completed").length,
    all: subs.length,
    review: reviews.length,
  }), [subs, teamBall, reviews]);

  const active = QUEUE_FILTERS.find((f) => f.key === filter)!;
  const queuePending = filter === "review" ? reviewsQ.isPending : subsQ.isPending;
  const queueCount = filter === "review" ? reviews.length : filtered.length;

  const inboxQ = useQuery({
    queryKey: ["inbox"], queryFn: () => apiClient.inbox(),
    enabled: isStaff, staleTime: 15_000,
  });
  const awaitingRows: InboxRow[] = (inboxQ.data ?? []).filter((r) => !!r.awaiting_reply_since);

  const convQ = useQuery({
    queryKey: ["conversation"], queryFn: () => apiClient.submissionConversation(),
    enabled: !isStaff, staleTime: 15_000,
  });
  const customerUnread = !isStaff
    && computeUnread(lastTeamMessageAt(convQ.data), getSeenWatermark(), false);

  const due = useDueAssets();

  // Opening a งาน from nothing. The button and its modal came with the queue
  // when /submissions retired (2026-09-01): the file room can only open a งาน
  // FROM files and the chat dock only from a conversation, so this is the one
  // free-form door and it had to land wherever the queue landed. A route may
  // retire; a capability may not retire with it.
  const [creating, setCreating] = useState(false);

  return (
    <div className="absolute inset-0 overflow-auto" style={{ background: "var(--paper)" }}>
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="font-mono uppercase mb-1.5" style={{ fontSize: 10.5, letterSpacing: ".24em", color: "var(--accent-deep)", fontWeight: 600 }}>
          / ถาดงานวันนี้
        </div>
        <h1 className="font-display font-medium m-0 mb-6" style={{ fontSize: 26, color: "var(--ink-1)", letterSpacing: "-.02em" }}>
          {isStaff ? "วันนี้" : `สวัสดี, ${user?.full_name || user?.username || ""}`}
        </h1>

        {isStaff ? (
          <>
            <div className="flex items-start justify-between gap-3 mb-3">
              <QueueFilters filter={filter} counts={counts} onPick={setFilter} />
              <button type="button" data-testid="today-new-job" onClick={() => setCreating(true)}
                      className="font-mono uppercase inline-flex items-center gap-1.5 px-3 py-1.5 shrink-0 transition hover:brightness-95 active:translate-y-px"
                      style={{ fontSize: 9.5, letterSpacing: ".1em", color: "var(--ink-1)",
                               background: "var(--accent)", border: "1px solid var(--accent)", fontWeight: 700 }}>
                <Plus size={12} /> งานใหม่
              </button>
            </div>

            {/* No .slice() cap: this IS the work queue now, not a digest of
                it. A queue that silently shows 8 of 40 is the same class of
                bug as a hardcoded number quietly winning — the page already
                scrolls. */}
            <Section icon={ClipboardList} title={active.title}
                     hint={queueCount ? (filter === "review" ? `${queueCount} รายการ` : `${queueCount} งาน — เก่าสุดก่อน`) : undefined}>
              {queuePending ? (
                <Empty>กำลังโหลด…</Empty>
              ) : queueCount === 0 ? (
                <Empty>{active.empty}</Empty>
              ) : filter === "review" ? (
                reviews.map((r) => (
                  <Row key={r.asset_id} data-testid={`today-review-${r.asset_id}`}
                       onClick={() => navigate(`/assets/${r.asset_id}`)}
                       primary={r.asset_tag} meta={`${r.plant_code || "—"} · ${r.requested_by || "—"}`}
                       tail={
                         <span className="font-mono uppercase" style={{
                           fontSize: 9, fontWeight: 700, letterSpacing: ".06em",
                           color: r.status === "in_review" ? "var(--ok)" : "var(--warn)" }}>
                           {r.status === "in_review" ? "กำลังตรวจ" : "ขอให้ตรวจ"}
                         </span>
                       } />
                ))
              ) : (
                filtered.map((s) => (
                  <Row key={s.id} data-testid={`today-work-${s.id}`}
                       onClick={() => navigate(`/jobs/${s.id}`)}
                       primary={s.title} meta={`${s.ref} · ${s.created_by}`}
                       tail={daysWaiting(s.updated_at ?? s.created_at)} />
                ))
              )}
            </Section>

            <QuietLine icon={Clock} label="งานที่รอลูกค้า" count={customerBall.length} />

            <Section icon={MessageSquare} title="บทสนทนาที่ยังไม่ตอบ" hint={awaitingRows.length ? `${awaitingRows.length} ราย` : undefined}>
              {inboxQ.isPending ? (
                <Empty>กำลังโหลด…</Empty>
              ) : awaitingRows.length === 0 ? (
                <Empty>ตอบครบแล้ว</Empty>
              ) : (
                awaitingRows.slice(0, 8).map((r) => (
                  <Row key={r.tenant_id} data-testid={`today-inbox-${r.tenant_id}`}
                       onClick={() => navigate(`/inbox?t=${r.tenant_id}`)}
                       primary={r.name} meta={r.last_preview ?? ""}
                       tail={<AwaitingBadge since={r.awaiting_reply_since!} />} />
                ))
              )}
            </Section>
          </>
        ) : (
          <>
            <Section icon={ClipboardList} title="งานที่รอคุณ" hint={customerBall.length ? `${customerBall.length} งาน` : undefined}>
              {subsQ.isPending ? (
                <Empty>กำลังโหลด…</Empty>
              ) : customerBall.length === 0 ? (
                <Empty>ไม่มีงานที่รอคุณตอนนี้</Empty>
              ) : (
                customerBall.slice(0, 8).map((s) => (
                  <Row key={s.id} data-testid={`today-work-${s.id}`}
                       onClick={() => openChatForTenant(false, null)}
                       primary={s.title} meta={s.ref} tail={daysWaiting(s.updated_at ?? s.created_at)} />
                ))
              )}
            </Section>

            <QuietLine icon={Clock} label="งานที่ทีมกำลังดำเนินการ" count={teamBall.length} />

            <Section icon={MessageSquare} title="บทสนทนา">
              {convQ.isPending ? (
                <Empty>กำลังโหลด…</Empty>
              ) : customerUnread ? (
                <Row data-testid="today-conversation-unread"
                     onClick={() => openChatForTenant(false, null)}
                     primary="มีข้อความใหม่จากทีม" meta="" tail={undefined} />
              ) : (
                <Empty>ไม่มีข้อความใหม่</Empty>
              )}
            </Section>
          </>
        )}

        <Section icon={FileWarning} title="Assets ใกล้ครบกำหนด / เกินกำหนด" hint={due.rows.length ? `${due.rows.length} รายการ` : undefined}>
          {due.isPending ? (
            <Empty>กำลังโหลด…</Empty>
          ) : due.rows.length === 0 ? (
            <Empty>ไม่มี asset ใกล้ครบกำหนด</Empty>
          ) : (
            due.rows.slice(0, 8).map((r) => (
              <Row key={r.asset_id} data-testid={`today-due-${r.asset_id}`}
                   onClick={() => navigate(`/assets/${r.asset_id}`)}
                   primary={r.tag} meta={`${r.plant_name} · ${r.authority || r.regulation}`}
                   tail={
                     <span className="font-mono uppercase" style={{
                       fontSize: 9, fontWeight: 700, letterSpacing: ".06em",
                       color: r.state === "overdue" ? "var(--err)" : "var(--warn)" }}>
                       {r.state === "overdue" ? "เกินกำหนด" : "ใกล้ครบกำหนด"}
                     </span>
                   } />
            ))
          )}
        </Section>
      </div>

      {creating && (
        <CreateModal onClose={() => setCreating(false)}
          onCreated={(d) => { setCreating(false); navigate(`/jobs/${d.id}`); }} />
      )}
    </div>
  );
}

// The filter row, lifted from SubmissionsScreen's left half — same six
// statuses, same critical treatment for a non-zero OVERDUE, same token
// vocabulary (accent as TEXT is --accent-deep; the tint fill keeps --accent).
function QueueFilters({ filter, counts, onPick }: {
  filter: QueueFilter; counts: Record<QueueFilter, number>; onPick: (f: QueueFilter) => void;
}): JSX.Element {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {QUEUE_FILTERS.map((f) => {
        const crit = f.key === "overdue" && counts.overdue > 0;
        const on = filter === f.key;
        return (
          <button key={f.key} type="button" data-testid={`today-filter-${f.key}`} onClick={() => onPick(f.key)}
            className="font-mono uppercase px-2 py-1 border transition-colors"
            style={{ fontSize: 8.5, letterSpacing: ".08em",
                     borderColor: on ? (crit ? "var(--err)" : "var(--accent)") : (crit ? "rgba(192,57,43,0.4)" : "var(--hairline)"),
                     color: on ? (crit ? "var(--err)" : "var(--accent-deep)") : (crit ? "var(--err)" : "var(--ink-3)"),
                     background: on ? (crit ? "rgba(192,57,43,0.08)" : "rgba(var(--accent-rgb) / 0.08)") : "transparent" }}>
            {f.label} {counts[f.key] ? `· ${counts[f.key]}` : ""}
          </button>
        );
      })}
    </div>
  );
}

function Section({ icon: Icon, title, hint, children }: {
  icon: LucideIcon; title: string; hint?: string; children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={13} style={{ color: "var(--accent)" }} />
        <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".14em", color: "var(--ink-3)", fontWeight: 700 }}>
          {title}
        </span>
        {hint && <span className="font-mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>· {hint}</span>}
      </div>
      <div style={{ border: "1px solid var(--hairline)", background: "var(--paper-2)" }}>
        {children}
      </div>
    </div>
  );
}

function QuietLine({ icon: Icon, label, count }: { icon: LucideIcon; label: string; count: number }): JSX.Element {
  return (
    <div className="flex items-center gap-2 mb-6 px-0.5 font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>
      <Icon size={12} />
      <span>{label}</span>
      <span style={{ color: "var(--ink-3)", fontWeight: 700 }}>{count}</span>
    </div>
  );
}

function Row({ primary, meta, tail, onClick, "data-testid": testId }: {
  primary: string; meta: string; tail?: React.ReactNode; onClick: () => void; "data-testid"?: string;
}): JSX.Element {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left border-b last:border-b-0 transition-colors hover:bg-[rgba(31,78,121,0.04)]"
            style={{ borderColor: "var(--hairline)" }}>
      <div className="min-w-0 flex-1">
        <div className="font-mono truncate" style={{ fontSize: 12.5, color: "var(--ink-1)", fontWeight: 600 }}>{primary}</div>
        {meta && <div className="font-mono truncate" style={{ fontSize: 10.5, color: "var(--ink-4)" }}>{meta}</div>}
      </div>
      {tail}
      <ArrowRight size={12} style={{ color: "var(--ink-4)", flexShrink: 0 }} />
    </button>
  );
}

function Empty({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="px-3.5 py-4 font-mono text-center" style={{ fontSize: 11, color: "var(--ink-3)" }}>{children}</div>;
}
